import { CommonModule, DatePipe, NgFor, NgTemplateOutlet } from "@angular/common";
import { Component, effect, EventEmitter, Inject, inject, input, Optional, Output, signal, computed } from "@angular/core";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { firstValueFrom, filter } from "rxjs";
import { HttpEventType, HttpResponse } from "@angular/common/http";
import { ConnectionService } from "../../services/connection.service";
import { OfflineModeService } from "../../services/offline-mode.service";
import { StorageService } from "../../services/storage/storage.service";
import { PROJECT_PROVIDER, DATA_PROVIDER, DataProvider, AUTH_PROVIDER, CONFIRM_SERVICE, IConfirmService, GEODATA_API_SERVICE, IGeodataApiService, OFFLINE_DETAIL_COMPONENT, DOWNLOAD_PROGRESS_COMPONENT, PROJECT_ROLES_CONFIG, ProjectRolesConfig, OFFLINE_DOCUMENT_GROUPS, OfflineDocumentGroup, PROJECT_ENTITY_LABEL, PROJECT_DELETION_HANDLER, ProjectDeletionHandler } from "../../core";
import { LayerService, Region } from "../services/layer.service";
import { MapStatusService } from "../services/map-status.service";
import { OfflineLayerService } from "../services/offline-layer.service";
import { UpdateService } from "../../services/update.service";
import { ProjectNewComponent } from "./new/project-new.component";
import { LayerStoreComponent } from "../layer-store/layer-store.component";
import { ProjectPointSelection, BoundsSelection } from "./project-points.component";
import { ProjectPointsService } from "./project-points.service";

export type DownloadStatus = 'none' | 'partial' | 'complete' | 'geodata_missing';

interface StatusCacheEntry {
	status: DownloadStatus;
	timestamp: number;
}

@Component({
    selector: 'bp-projects',
    templateUrl: 'projects.component.html',
    styleUrls: ['projects.component.css', 'project-manager-section.style.css'],
		imports: [CommonModule, NgFor, NgTemplateOutlet, DatePipe, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem],
    standalone: true
})
export class ProjectsComponent {
	activeProject = input<string>();
	tmpProjects?: any[];
	@Output() selectProject = new EventEmitter<string>();

	// Download-Status pro Projekt (Map von projectId zu Status)
	projectDownloadStatus = signal<Map<string, DownloadStatus>>(new Map());
	// True wenn Download-Status initial ermittelt wurde
	statusInitialized = signal<boolean>(false);
	// True während Offline-Modus vorbereitet wird (nach Modal-Schließung)
	isPreparingOffline = signal<boolean>(false);

	// Status-Cache mit Zeitstempel (TTL-basiert)
	private statusCache = new Map<string, StatusCacheEntry>();
	private readonly STATUS_CACHE_TTL_MS = 10000; // 10 Sekunden

	// Online: verfügbar wenn Netzwerk online
	onlineAvailable = computed(() => this.oms.networkOnline());
	// Online: aktiv wenn nicht im Offline-Modus
	onlineActive = computed(() => !this.oms.offlineMode());

	// Offline: verfügbar wenn aktives Projekt heruntergeladen ist (complete oder geodata_missing)
	offlineAvailable = computed(() => {
		const activeId = this.activeProject();
		if (!activeId) return false;
		const status = this.projectDownloadStatus().get(activeId);
		return status === 'complete' || status === 'geodata_missing';
	});
	// Offline: aktiv wenn im Offline-Modus
	offlineActive = computed(() => this.oms.offlineMode());

	// Gefilterte Projektliste: Im Offline-Modus nur heruntergeladene Projekte
	// Bei Netzwerk online: auch partial (zum Nachladen), sonst nur complete
	filteredProjectList = computed(() => {
		const projects = this.ps.projectList();
		if (this.oms.offlineMode()) {
			if (this.oms.networkOnline()) {
				return projects.filter((p: any) => {
					const status = this.projectDownloadStatus().get(p._id);
					return status === 'complete' || status === 'partial';
				});
			}
			return projects.filter((p: any) => this.projectDownloadStatus().get(p._id) === 'complete');
		}
		return projects;
	});

	get canManageProjects(): boolean {
		return this.auth.canI('manage', 'projects');
	}

	get canCreateProjects(): boolean {
		// Jeder authentifizierte Benutzer kann Projekte erstellen
		return this.auth.isAuthenticated.value;
	}

	/**
	 * Prüft, ob der aktuelle Benutzer das aktive Projekt löschen darf.
	 * Erlaubt für Manager oder Owner des Projekts.
	 */
	get canDeleteActiveProject(): boolean {
		if (this.canManageProjects) return true;
		const activeId = this.activeProject();
		if (!activeId) return false;
		const project = this.ps.projectList().find((p: any) => p._id === activeId);
		return project?.owner?.id === this.auth.currentUserId;
	}

	/** Prüft ob ein Projekt Mitglieder in irgendeiner Rolle hat */
	hasAnyMembers(prj: any): boolean {
		return this.rolesConfig.memberRoles.some(r => prj[r.field]?.length);
	}

	/**
	 * Formatiert eine Liste von Projektmitgliedern für die Anzeige.
	 * Zeigt max. 2 Namen an, danach "+N".
	 */
	formatMembers(members: { username: string }[]): string {
		if (!members || members.length === 0) return '';
		if (members.length <= 2) {
			return members.map(m => m.username).join(', ');
		}
		return `${members[0].username}, ${members[1].username} +${members.length - 2}`;
	}

	entityLabel = inject(PROJECT_ENTITY_LABEL);
	private deletionHandler = inject(PROJECT_DELETION_HANDLER, { optional: true }) as ProjectDeletionHandler | null;

	constructor(
		private modalService: NgbModal,
		@Inject(PROJECT_PROVIDER) public ps: any,
		private cs: ConnectionService,
		@Inject(DATA_PROVIDER) private ds: DataProvider,
		private ls: LayerService,
		private ols: OfflineLayerService,
		private storage: StorageService,
		private mapStatusService: MapStatusService,
		public oms: OfflineModeService,
		@Inject(AUTH_PROVIDER) private auth: any,
		@Inject(CONFIRM_SERVICE) private confirmService: IConfirmService,
		@Inject(OFFLINE_DETAIL_COMPONENT) private offlineDetailComponent: any,
		@Inject(DOWNLOAD_PROGRESS_COMPONENT) private downloadProgressComponent: any,
		private pps: ProjectPointsService,
		@Inject(PROJECT_ROLES_CONFIG) public rolesConfig: ProjectRolesConfig,
		private updateService: UpdateService,
		@Inject(OFFLINE_DOCUMENT_GROUPS) private offlineDocGroups: OfflineDocumentGroup[],
		@Optional() @Inject(GEODATA_API_SERVICE) private geodataApi: IGeodataApiService | null
	) {
		// Events die echte Downloads abgeschlossen haben -> Cache invalidieren + Status neu berechnen
		this.ols.seedAllCompleted.subscribe(() => {
			this.invalidateStatusCache(this.activeProject());
			this.updateActiveProjectStatusFast('tiles');
		});
		this.ds.pointsDownloadCompleted.subscribe(() => {
			this.invalidateStatusCache(this.activeProject());
			this.updateActiveProjectStatusFast('points');
		});
		this.ds.downloadedPointsDiscarded.subscribe(() => {
			this.invalidateStatusCache(this.activeProject());
			this.updateActiveProjectStatus();
		});
		this.ls.attachmentsChanged.subscribe(() => {
			this.invalidateStatusCache(this.activeProject());
			this.updateActiveProjectStatusFast('geodata');
		});

		// Events die nur UI-Änderungen sind -> Cache nutzen (kein Invalidieren)
		this.ls.layersDefined.subscribe(() => this.updateAllDownloadStatus());
		this.ls.layersChanged.subscribe(() => this.updateActiveProjectStatus());
		this.ls.regionsChanged.subscribe(() => this.onRegionsChanged());

		// Discard invalidiert alle Caches
		this.ps.discarded.subscribe(() => {
			this.invalidateStatusCache();
			this.updateAllDownloadStatus();
		});

		// Update status when project list changes
		effect(() => {
			const projects = this.ps.projectList();
			if (projects.length > 0) {
				this.updateAllDownloadStatus();
			}
		});
	}

	/**
	 * Gibt den Download-Status für ein Projekt zurück
	 */
	getDownloadStatus(projectId: string): DownloadStatus {
		return this.projectDownloadStatus().get(projectId) ?? 'none';
	}

	/**
	 * Aktualisiert den Download-Status nur für das aktive Projekt (schnell).
	 * @param forceRefresh Wenn true, wird der Cache ignoriert
	 */
	private async updateActiveProjectStatus(forceRefresh = false): Promise<void> {
		const activeProjectId = this.activeProject();
		if (!activeProjectId) return;

		const status = await this.calculateProjectStatus(activeProjectId, true, forceRefresh);

		this.projectDownloadStatus.update(map => {
			const newMap = new Map(map);
			newMap.set(activeProjectId, status);
			return newMap;
		});
		this.statusInitialized.set(true);
	}

	/**
	 * Status-Aktualisierung nach bekanntem Download-Abschluss.
	 * Berechnet den Status immer vollständig neu (Cache bereits invalidiert).
	 */
	private async updateActiveProjectStatusFast(_completed: 'tiles' | 'points' | 'geodata'): Promise<void> {
		// forceRefresh=true da Cache bereits invalidiert wurde
		await this.updateActiveProjectStatus(true);
	}

	/**
	 * Berechnet den Download-Status für ein einzelnes Projekt.
	 * @param forceRefresh Wenn true, wird der Cache ignoriert
	 */
	private async calculateProjectStatus(projectId: string, isActive: boolean, forceRefresh = false): Promise<DownloadStatus> {
		// Cache prüfen (nur für nicht-aktive Projekte oder wenn nicht forceRefresh)
		if (!forceRefresh) {
			const cached = this.statusCache.get(projectId);
			if (cached && (Date.now() - cached.timestamp) < this.STATUS_CACHE_TTL_MS) {
				return cached.status;
			}
		}

		let tilesStatus: DownloadStatus;
		let geodataStatus: 'none' | 'partial' | 'complete' | 'not_applicable';
		let pointsDownloaded: boolean;

		if (isActive) {
			// Nur aktive Bereiche für Status-Berechnung (konsistent mit Download)
			const allRegions = this.ls.regions()?.regions ?? [];
			const activeIndices = this.pps.activeRegionIndices();
			const activeRegions = activeIndices.length > 0
				? activeIndices.filter(i => i < allRegions.length).map(i => allRegions[i])
				: allRegions;  // Fallback: alle Bereiche wenn keine explizit aktiv
			const regionBounds = activeRegions.map(r => r.bounds);
			const mapDef = this.ls.mapDef();
			[tilesStatus, geodataStatus, pointsDownloaded] = await Promise.all([
				this.ols.getDownloadStatusForProject(projectId, regionBounds, mapDef),
				this.ols.getGeodataFilesStatus(projectId, mapDef),
				this.ds.arePointsDownloadedForProject(projectId)
			]);
		} else {
			[tilesStatus, geodataStatus, pointsDownloaded] = await Promise.all([
				this.ols.getDownloadStatusForProject(projectId),
				this.ols.getGeodataFilesStatus(projectId),
				this.ds.arePointsDownloadedForProject(projectId)
			]);
		}

		// Kombiniere Status: Tiles + Punkte + Geodaten
		// Punkte sind wichtiger als Tiles - Offline-Modus ist nutzbar wenn Punkte da sind
		let combinedStatus: DownloadStatus;

		if (pointsDownloaded) {
			// Punkte vorhanden - Basis ist OK
			if (tilesStatus === 'complete') {
				combinedStatus = 'complete';
			} else if (tilesStatus === 'partial') {
				combinedStatus = 'partial';
			} else {
				// tilesStatus === 'none' - Tiles nicht heruntergeladen, aber Punkte da
				// Das ist OK für Offline-Modus (Karte zeigt Asset-Tiles oder Online-Fallback)
				combinedStatus = 'complete';
			}
		} else {
			// Keine Punkte - Status hängt von Tiles ab
			if (tilesStatus === 'complete') {
				combinedStatus = 'partial'; // Tiles da, aber keine Punkte
			} else {
				combinedStatus = tilesStatus; // 'none' oder 'partial'
			}
		}

		// Geodaten-Status berücksichtigen
		if (geodataStatus !== 'not_applicable') {
			if (combinedStatus === 'complete' && geodataStatus !== 'complete') {
				combinedStatus = 'geodata_missing';
			} else if (combinedStatus !== 'none' && geodataStatus === 'none') {
				combinedStatus = 'partial';
			} else if (geodataStatus === 'partial') {
				if (combinedStatus === 'complete') {
					combinedStatus = 'geodata_missing';
				} else if (combinedStatus !== 'none') {
					combinedStatus = 'partial';
				}
			}
		}

		// Cache aktualisieren
		this.statusCache.set(projectId, {
			status: combinedStatus,
			timestamp: Date.now()
		});

		return combinedStatus;
	}

	/**
	 * Invalidiert den Status-Cache für ein Projekt oder alle Projekte.
	 */
	private invalidateStatusCache(projectId?: string): void {
		if (projectId) {
			this.statusCache.delete(projectId);
		} else {
			this.statusCache.clear();
		}
	}

	// Debounce für updateAllDownloadStatus
	private updateStatusTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private isStatusUpdateRunning = false;

	/**
	 * Aktualisiert den Download-Status für alle Projekte.
	 * Berechnet nur das aktive Projekt sofort, andere verwenden den Cache.
	 */
	private async updateAllDownloadStatus(): Promise<void> {
		// Debounce: Wenn innerhalb von 100ms mehrere Aufrufe kommen, nur einmal ausführen
		if (this.updateStatusTimeoutId) {
			clearTimeout(this.updateStatusTimeoutId);
		}

		this.updateStatusTimeoutId = setTimeout(async () => {
			// Verhindere parallele Ausführung
			if (this.isStatusUpdateRunning) return;
			this.isStatusUpdateRunning = true;

			try {
				const projects = this.ps.projectList();
				const newStatusMap = new Map<string, DownloadStatus>();
				const activeProjectId = this.activeProject();

				// Aktives Projekt immer frisch berechnen
				for (const project of projects) {
					if (project._id === activeProjectId) {
						const status = await this.calculateProjectStatus(project._id, true);
						newStatusMap.set(project._id, status);
					} else {
						// Nicht-aktive Projekte: Cache verwenden, sonst 'none'
						const cached = this.statusCache.get(project._id);
						const status = cached ? cached.status : 'none';
						newStatusMap.set(project._id, status);
					}
				}

				this.projectDownloadStatus.set(newStatusMap);
				this.statusInitialized.set(true);
			} finally {
				this.isStatusUpdateRunning = false;
			}
		}, 100);
	}

	async toggleOfflineMode(): Promise<void> {
		if (this.offlineActive()) {
			// Wechsel zu Online (nur möglich wenn Netzwerk verfügbar)
			if (this.oms.canGoOnline()) {
				await this.oms.setOfflineMode(false);
			}
		} else {
			// Wechsel zu Offline (nur möglich wenn offline verfügbar)
			if (this.offlineAvailable()) {
				// SW-Versionscheck: Warnen wenn ein Update heruntergeladen wird oder verfügbar ist
				const swStatus = this.updateService.status();
				if (swStatus === 'downloading') {
					const proceed = await this.confirmService.confirm({
						title: 'App-Update wird heruntergeladen',
						message: 'Ein App-Update wird gerade heruntergeladen. Wenn Sie jetzt offline gehen, wird die veraltete Version verwendet und es kann zu Fehlern kommen.\n\nBitte warten Sie, bis das Update abgeschlossen ist.',
						confirmText: 'Trotzdem offline gehen',
						cancelText: 'Warten',
						confirmClass: 'btn-warning'
					});
					if (!proceed) return;
				} else if (swStatus === 'checking') {
					await this.confirmService.alert(
						'Die App prüft gerade auf Updates. Bitte versuchen Sie es in wenigen Sekunden erneut.',
						'Updateprüfung läuft'
					);
					return;
				} else if (this.updateService.updateReady()) {
					const update = await this.confirmService.confirm({
						title: 'App-Update verfügbar',
						message: 'Ein App-Update wurde heruntergeladen. Bitte aktualisieren Sie die App vor dem Offline-Wechsel, um Versionsprobleme zu vermeiden.\n\nDie App wird nach dem Update neu geladen.',
						confirmText: 'Jetzt aktualisieren',
						cancelText: 'Trotzdem offline gehen',
						confirmClass: 'btn-success'
					});
					if (update) {
						this.updateService.activateUpdate();
						return;
					}
				}

				// Projekt-Dokument für Offline herunterladen
				const activeId = this.activeProject();
				if (activeId) {
					await this.ps.downloadProjectForOffline(activeId);
				}
				await this.oms.setOfflineMode(true);
			}
		}
	}

	onProjectNew($event: Event): void {
		const modalRef = this.modalService.open(ProjectNewComponent, { scrollable: true, backdrop: 'static' });
		modalRef.closed.subscribe((data: any) => { });
		$event.stopPropagation();
	}

	onProjectEdit($event: Event): void {
		const activeId = this.activeProject();
		if (!activeId) return;

		const modalRef = this.modalService.open(ProjectNewComponent, { scrollable: true, backdrop: 'static' });
		modalRef.componentInstance.editProjectId = activeId;
		modalRef.closed.subscribe((data: any) => { });
		$event.stopPropagation();
	}

	async onDownload(): Promise<void> {
		const activeId = this.activeProject();
		if (!activeId) return;

		// Sofort visuelles Feedback zeigen
		this.isPreparingOffline.set(true);

		// Alle Vorbereitungen parallel ausführen für maximale Geschwindigkeit
		try {
			const [, , saved, pl] = await Promise.all([
				// Server-Daten aktualisieren (parallel)
				this.ps.refreshProjectList(),
				this.ps.refreshProjectDataFromRemote(activeId),
				// Lokale Daten laden (parallel)
				this.ps.getProjectValue('projectPoints') as Promise<ProjectPointSelection | null>,
				this.ps.getProjectValue('offlinePlusLevels') as Promise<number | null>
			]);

			// Zeichnungslayer komplett neu laden
			this.ls.zeichnungslayerReloadRequested.emit();

			// Projektpunkte-Konfiguration auswerten
			const boundsPointIds = this.getBoundsPointIds(saved);
			const activeRegions = this.getActiveRegions(saved);

			// Nur aktive Bereiche für Status-Berechnung (konsistent mit Download)
			const allProjectRegions = this.ls.regions()?.regions ?? [];
			const activeRegionIndices = this.pps.activeRegionIndices();
			const projectRegions = activeRegionIndices.length > 0
				? activeRegionIndices.filter(i => i < allProjectRegions.length).map(i => allProjectRegions[i])
				: allProjectRegions;  // Fallback: alle Bereiche wenn keine explizit aktiv
			const projectRegionBounds = projectRegions.map(r => r.bounds);
			const mapDef = this.ls.mapDef();

			let plusLevels: 0|1|2 = (pl ?? 0) as 0|1|2;
			let needsTileDownload = projectRegionBounds.length > 0;
			const needsPointsDownload = true;
			let docGroupLabels: string[] = [];

			// Zoomstufen-Dialog SOFORT öffnen, Status-Berechnung läuft parallel im Hintergrund
			if (projectRegionBounds.length > 0) {
				// Status-Berechnung als Promise starten (läuft parallel während Dialog offen ist)
				const statusPromise = Promise.all([
					this.ols.getDownloadStatusForProject(activeId, projectRegionBounds, mapDef),
					this.ols.getMaxDownloadedZoomLevel()
				]).then(([tilesStatus, currentMaxZoom]) => ({
					tilesAlreadyComplete: tilesStatus === 'complete',
					currentMaxZoom
				}));

				// Modal SOFORT öffnen - Status wird asynchron geladen
				const modalRef = this.modalService.open(this.offlineDetailComponent);
				modalRef.componentInstance.plusLevels = plusLevels;
				modalRef.componentInstance.regions = projectRegionBounds;
				modalRef.componentInstance.statusPromise = statusPromise;

				try {
					const result = await firstValueFrom(modalRef.closed);
					// Neues Format: { plusLevels, skipDownload, downloadDocGroups }
					if (typeof result === 'object' && result !== null) {
						plusLevels = result.plusLevels ?? 0;
						if (result.skipDownload) {
							needsTileDownload = false;
						} else {
							// Nutzer möchte erneut herunterladen (auch wenn bereits komplett)
							needsTileDownload = true;
						}
						docGroupLabels = result.downloadDocGroups ?? [];
					} else {
						// Altes Format (Kompatibilität)
						plusLevels = result ?? 0;
					}
					await this.ps.setProjectValue('offlinePlusLevels', plusLevels);
				} catch {
					// Modal dismissed - do nothing
					return;
				}
			}

			// Fortschritts-Dialog immer öffnen
			const progressModalRef = this.modalService.open(this.downloadProgressComponent, {
				backdrop: 'static',
				keyboard: false
			});
			progressModalRef.componentInstance.skipTileDownload = !needsTileDownload;
			progressModalRef.componentInstance.skipPointsDownload = !needsPointsDownload;
			progressModalRef.componentInstance.expectedPointsCount = boundsPointIds.length;

			// Lade Karten-Tiles herunter (nur wenn nötig)
			if (needsTileDownload) {
				this.ols.seedRequest.emit(plusLevels);
			}

			// Warte auf DataService-Initialisierung (wichtig bei leeren Browserdaten)
			await this.ds.waitForInitialization();

			// Lade Punkte herunter (nur wenn nötig)
			if (needsPointsDownload) {
				const hasPoints = boundsPointIds.length > 0 || activeRegions.length > 0;
				if (hasPoints) {
					if (boundsPointIds.length > 0) {
						await this.ds.downloadDocumentsByIds(boundsPointIds);
					}
					if (activeRegions.length > 0) {
						await this.ds.downloadDocuments(activeRegions);
					}
				} else {
					await this.ds.ensureEmptyOfflineDatabase();
				}
			}

			// Fehlende Geodaten vom Server holen
			if (this.geodataApi && this.oms.networkOnline()) {
				await this.downloadMissingGeodata(activeId, mapDef);
			}

			// Lade Projekt-Dokument herunter
			await this.ps.downloadProjectForOffline(activeId);

			// Lade optionale Dokument-Gruppen herunter (z.B. SEA-PDFs)
			if (docGroupLabels.length > 0) {
				const groups = this.offlineDocGroups.filter(g => docGroupLabels.includes(g.label));
				const allUrls = groups.flatMap(g => g.urls);
				for (const url of allUrls) {
					try {
						const cached = await caches.match(url);
						if (!cached) await fetch(url);
					} catch { /* ignore */ }
				}
			}

			// Warten bis Benutzer Modal schließt
			await progressModalRef.result.catch(() => {});

			// Zeige "Bereite Offline-Modus vor..." Status
			this.isPreparingOffline.set(true);

			// Status-Events emittieren wenn Downloads übersprungen wurden
			if (!needsTileDownload) {
				this.ols.seedAllCompleted.emit();
			}
			if (!needsPointsDownload) {
				this.ds.pointsDownloadCompleted.emit();
			}

			// Optimistische Status-Ermittlung
			let newStatus: DownloadStatus;
			const pointsOk = needsPointsDownload || this.projectDownloadStatus().get(activeId) !== 'none';

			if (pointsOk) {
				const geodataStatus = await this.ols.getGeodataFilesStatus(activeId, this.ls.mapDef());
				newStatus = (geodataStatus === 'not_applicable' || geodataStatus === 'complete') ? 'complete' : 'geodata_missing';
			} else {
				newStatus = await this.calculateProjectStatus(activeId, true);
			}

			// Cache aktualisieren
			this.projectDownloadStatus.update(map => {
				const newMap = new Map(map);
				newMap.set(activeId, newStatus);
				return newMap;
			});

			if (newStatus === 'complete' || newStatus === 'geodata_missing') {
				await this.oms.setOfflineMode(true);
			} else if (newStatus === 'partial') {
				const goOffline = await this.confirmService.confirm({
					title: 'Offline-Modus aktivieren?',
					message: 'Die Kartenkacheln wurden nicht vollständig heruntergeladen. Möchten Sie trotzdem in den Offline-Modus wechseln?',
					confirmText: 'Trotzdem wechseln',
					cancelText: 'Abbrechen'
				});
				if (goOffline) {
					await this.oms.setOfflineMode(true);
				}
			}
		} catch (err) {
			console.error('[onDownload] Fehler:', err);
		} finally {
			this.isPreparingOffline.set(false);
		}
	}

	/**
	 * Extrahiert die Punkt-IDs aus aktiven Bounds-Selections.
	 */
	private getBoundsPointIds(saved: ProjectPointSelection | null): string[] {
		if (!saved) return [];

		const ids = new Set<string>();

		// Bounds-Selections (aktive)
		if (saved.boundsSelections) {
			saved.boundsSelections
				.filter((s: BoundsSelection) => s.active)
				.forEach((s: BoundsSelection) => s.pointIds.forEach(id => ids.add(id)));
		}
		// Migration: alte fromBounds Struktur
		if ((saved as any).fromBounds && !saved.boundsSelections) {
			((saved as any).fromBounds as string[]).forEach(id => ids.add(id));
		}

		return [...ids];
	}

	/**
	 * Gibt die aktiven Projektbereiche (Regions) zurück.
	 * Wenn keine explizit aktiviert sind, werden alle Projektbereiche verwendet.
	 * Gibt jetzt vollständige Region-Objekte zurück (mit Polygon-Koordinaten).
	 */
	private getActiveRegions(saved: ProjectPointSelection | null): Region[] {
		const allRegions = this.ls.regions()?.regions ?? [];
		if (allRegions.length === 0) return [];

		// Wenn keine Konfiguration oder keine explizit aktivierten Regions,
		// verwende alle Projektbereiche
		const activeIndices = saved?.fromRegions ?? [];
		if (activeIndices.length === 0) {
			return allRegions;
		}

		return activeIndices
			.filter(i => i < allRegions.length)
			.map(i => allRegions[i]);
	}

	/**
	 * Öffnet den LayerStore um fehlende Geodaten-Dateien hinzuzufügen.
	 */
	openLayerStore(): void {
		const modalRef = this.modalService.open(LayerStoreComponent, { scrollable: true, backdrop: 'static' });
		modalRef.componentInstance.project = this.ps.project();
		modalRef.closed.subscribe((data: any) => {
			this.ls.saveLayersToLocalStorage(data);
		});
	}

	/**
	 * Wird aufgerufen, wenn sich die Projekt-Regionen ändern.
	 * Aktualisiert den Download-Status.
	 * HINWEIS: Punkte werden NICHT mehr automatisch verworfen - der User entscheidet
	 * selbst wann er neu herunterladen möchte.
	 */
	private async onRegionsChanged(): Promise<void> {
		this.updateAllDownloadStatus();
	}

	async onDiscardOfflineData(): Promise<void> {
		await this.ps.discardLocal();
		await this.ds.discardDownloadedPoints();
	}

	/**
	 * Lädt fehlende Geodaten (GPKG, GeoTIFF, GeoJSON) vom Server herunter.
	 * Prüft welche Dateien lokal fehlen und auf dem Server verfügbar sind.
	 */
	private async downloadMissingGeodata(projectId: string, mapDef: any): Promise<void> {
		if (!this.geodataApi || !mapDef?.layers) return;

		const geodataLayers = mapDef.layers.filter((l: any) =>
			['gpkg', 'geotiff', 'geojson'].includes(l.type)
		);
		if (geodataLayers.length === 0) return;

		// Prüfe welche Dateien lokal fehlen
		const missing: string[] = [];
		for (const ld of geodataLayers) {
			const buf = await this.ps.getProjectValue(ld.name, 'attachments');
			if (!buf) missing.push(ld.name);
		}
		if (missing.length === 0) return;

		// Prüfe was auf dem Server verfügbar ist
		try {
			const { files } = await firstValueFrom(this.geodataApi.list(projectId));
			const serverNames = new Set(files.map(f => f.name));
			const downloadable = missing.filter(name => serverNames.has(name));
			if (downloadable.length === 0) return;

			console.log(`[Offline] Lade ${downloadable.length} fehlende Geodatei(en) vom Server:`, downloadable);

			for (const filename of downloadable) {
				try {
					const response = await firstValueFrom(
						this.geodataApi.download(projectId, filename).pipe(
							filter(event => event.type === HttpEventType.Response)
						)
					);
					const buf = (response as HttpResponse<ArrayBuffer>).body;
					if (!buf) continue;

					// In IndexedDB speichern
					if (filename.endsWith('.geojson') || filename.endsWith('.json')) {
						const text = new TextDecoder().decode(buf);
						await this.ps.setProjectValue(filename, text, 'attachments');
					} else {
						await this.ps.setProjectValue(filename, buf, 'attachments');
					}
					await this.ps.addGeodataFile(filename);
					console.log(`[Offline] Geodatei heruntergeladen: ${filename}`);
				} catch (err) {
					console.warn(`[Offline] Download fehlgeschlagen für ${filename}:`, err);
				}
			}
			this.ls.attachmentsChanged.emit();
		} catch (err) {
			console.warn('[Offline] Geodaten-Liste konnte nicht vom Server geladen werden:', err);
		}
	}

	async onProjectDelete($event: Event): Promise<void> {
		$event.stopPropagation();
		const activeId = this.activeProject();
		if (!activeId) return;

		const project = this.ps.projectList().find((p: any) => p._id === activeId);
		const projectName = project?.name ?? this.entityLabel.unnamed;

		const confirmed = await this.confirmService.confirmDelete(
			`Möchten Sie ${this.entityLabel.accusativeArticle} "${projectName}" wirklich löschen?\n\nDies kann nicht rückgängig gemacht werden.`,
			`${this.entityLabel.name} löschen`
		);
		if (!confirmed) return;

		// Erst Offline-Daten verwerfen
		await this.onDiscardOfflineData();

		// Dann Projekt löschen (über Handler wenn vorhanden)
		try {
			const success = this.deletionHandler
				? await this.deletionHandler.deleteProject(activeId)
				: await this.ps.deleteProject(activeId);
			if (!success) {
				await this.confirmService.error(`Fehler beim Löschen`);
			}
		} catch (e) {
			await this.confirmService.error(
				`Fehler beim Löschen: ` + (e instanceof Error ? e.message : 'Unbekannter Fehler')
			);
		}
	}
}
